import { lazy, Suspense, useEffect, useState } from "react";
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";

import { AppRedirect, MrpBomToBomAppRedirect } from "./components/AppRedirect";
import { AuthenticatedHubShell } from "./components/AuthenticatedHubShell";
import { Layout } from "./components/Layout";
import { ModuleHome } from "./components/ModuleHome";
import { Protected } from "./components/Protected";
import { Dashboard } from "./pages/Dashboard";
import { FloorTabletHomePage } from "./pages/FloorTabletHomePage";
import { ErpNextSync } from "./pages/ErpNextSync";
import { EntityDetail } from "./pages/EntityDetail";
import { Findings } from "./pages/Findings";
import { Settings as ErpSettings } from "./pages/Settings";
import { DataControlRegistryPage } from "./pages/DataControlRegistryPage";
import { AccessNeededPage } from "./pages/AccessNeededPage";
import { SignIn } from "./pages/SignIn";
import { TabletPinPage } from "./pages/TabletPinPage";
import { NotificationsPage } from "./pages/NotificationsPage";
import { UserSettingsPage } from "./pages/UserSettingsPage";
import { Aml } from "./pages/mrp/Aml";
import { RedirectMrpSalesOrderId, RedirectMrpSalesOrders } from "./pages/mrp/RedirectMrpSalesOrders";
import { RedirectSalesOrdersToInsideSales } from "./pages/sales-orders/RedirectSalesOrdersToInsideSales";
import { buildMrpSkuDetailPath } from "./lib/mrp";
import { Assemblies } from "./pages/mrp/Assemblies";
import { ApproveBomRules } from "./pages/bom/ApproveBomRules";
import { BomBrowser } from "./pages/bom/BomBrowser";
import { SetBomRules } from "./pages/bom/SetBomRules";
import { BomTestRevisionPage } from "./pages/bom/BomTestRevisionPage";
import { PushToMegaventoryPage } from "./pages/bom/PushToMegaventoryPage";
import { BuildPlan } from "./pages/mrp/BuildPlan";
import { GhostBuildOrders } from "./pages/mrp/GhostBuildOrders";
import { ClampForecast } from "./pages/mrp/ClampForecast";
import { Planning } from "./pages/mrp/Planning";
import { TlaPlanningPage } from "./pages/mrp/TlaPlanning";
import { TlaPlanningDetailPage } from "./pages/mrp/TlaPlanningDetailPage";
import { IncomingParts } from "./pages/mrp/IncomingParts";
import { TransmutePage } from "./pages/mrp/TransmutePage";
import { PurchaseOrderList } from "./pages/mrp/PurchaseOrderList";
import { PurchaseOrderDetail } from "./pages/mrp/PurchaseOrderDetail";
import { AdjustmentDetailPage } from "./pages/mrp/AdjustmentDetailPage";
import { PurchaseInvoiceDetailPage } from "./pages/mrp/PurchaseInvoiceDetail";
import {
  PurchaseOrderComposeLegacyRedirect,
  createHubPurchaseOrderAndNavigate,
} from "./pages/mrp/purchaseOrderHubDraftNav";
import { RfqList } from "./pages/mrp/RfqList";
import { RfqDetail } from "./pages/mrp/RfqDetail";
import { SupplierListPage } from "./pages/mrp/SupplierList";
import { SupplierNewPage } from "./pages/mrp/SupplierNewPage";
import { SupplierLegacyDetailRedirect } from "./pages/mrp/SupplierDetail";
import { SkuDetail } from "./pages/mrp/SkuDetail";
import { Overstock } from "./pages/mrp/Overstock";
import { SafetyStockAlerts } from "./pages/mrp/SafetyStockAlerts";
import { Skus } from "./pages/mrp/Skus";
import { MrpSettings } from "./pages/mrp/Settings";
import { Locations } from "./pages/admin/Locations";
import { DistributorApis } from "./pages/admin/DistributorApis";
import { MegaventoryPaymentTermsLabels } from "./pages/admin/MegaventoryPaymentTermsLabels";
import { SubappAccessPage } from "./pages/admin/SubappAccessPage";
import { UserDirectory } from "./pages/admin/UserDirectory";
import { PeopleDirectoryPage } from "./pages/people/PeopleDirectoryPage";
import { PeopleAppsPage } from "./pages/people/PeopleAppsPage";
import { PeopleOrgChartPage } from "./pages/people/PeopleOrgChartPage";
import { PeoplePermissionsPage } from "./pages/people/PeoplePermissionsPage";
import { PeopleSkillsPage } from "./pages/people/PeopleSkillsPage";
import { PeopleTrainingMatrixPage } from "./pages/people/PeopleTrainingMatrixPage";
import { PersonPage } from "./pages/people/PersonPage";
import { AdminSettingsPage } from "./pages/admin/AdminSettingsPage";
import { ContactsPage } from "./pages/admin/ContactsPage";
import { HubSupportTicketsPage } from "./pages/admin/HubSupportTicketsPage";
import { LabelTemplatesPage } from "./pages/marketing/LabelTemplatesPage";
import { AdminTimeCardChangesPage } from "./pages/admin/AdminTimeCardChangesPage";
import { PdfTemplatesPage } from "./pages/admin/PdfTemplatesPage";
import { VendorPortalPage } from "./pages/admin/VendorPortalPage";
import { ServerAccessPage } from "./pages/admin/ServerAccessPage";
import { ServerAccessInvitePage } from "./pages/admin/ServerAccessInvitePage";
import { AdminLaborPage } from "./pages/admin/AdminLaborPage";
import { ProductScanLinksPage } from "./pages/admin/ProductScanLinksPage";
import { ZplPrintersPage } from "./pages/admin/ZplPrintersPage";
import { SubappAccessGuard } from "./components/SubappAccessGuard";
import { SubappAccessGuardAnyOf } from "./components/SubappAccessGuardAnyOf";
import { moduleKeysListingNavPath } from "./lib/appNavigation";
import { ClampQcHubPage } from "./pages/clampqc/ClampQcHubPage";
import { ClampQcPage } from "./pages/clampqc/ClampQcPage";
import { ClampQcReceivePage } from "./pages/clampqc/ClampQcReceivePage";
import { ClampQcReportingPage } from "./pages/clampqc/ClampQcReportingPage";
import { ClampQcReportingWorkOrderLinesPage } from "./pages/clampqc/ClampQcReportingWorkOrderLinesPage";
import { ClampQcSettingsPage } from "./pages/clampqc/ClampQcSettingsPage";
import { KittingPage } from "./pages/manufacturing/KittingPage";
import { KittingPickPage } from "./pages/manufacturing/KittingPickPage";
import { AlternatesPage } from "./pages/manufacturing/AlternatesPage";
import { ClampQcCreditRequestsPage } from "./pages/manufacturing/ClampQcCreditRequestsPage";
import { WirelessIssuePage } from "./pages/manufacturing/WirelessIssuePage";
import { WirelessCheckPage } from "./pages/manufacturing/WirelessCheckPage";
import { WirelessAssignmentsPage } from "./pages/manufacturing/WirelessAssignmentsPage";
import { ManufacturingReportingPage } from "./pages/manufacturing/ManufacturingReportingPage";
import { QuickBooksTimeOAuthCallbackPage } from "./pages/manufacturing/QuickBooksTimeOAuthCallbackPage";
import { PottingSendPage } from "./pages/manufacturing/PottingSendPage";
import { BoxLabelGeneratePage } from "./pages/manufacturing/BoxLabelGeneratePage";
import { SuntronicBoxLabelsPage } from "./pages/manufacturing/SuntronicBoxLabelsPage";
import { LaserMarkPage } from "./pages/manufacturing/LaserMarkPage";
import { ForwardDemandPage } from "./pages/manufacturing/ForwardDemandPage";
import { ProductionCapacityPage } from "./pages/manufacturing/ProductionCapacityPage";
import { ProductionCapacitySettingsPage } from "./pages/manufacturing/ProductionCapacitySettingsPage";
import { ProductionCalendarPage } from "./pages/manufacturing/ProductionCalendarPage";
import { WorkOrdersPage } from "./pages/manufacturing/WorkOrdersPage";
import { ConfigurationLayout } from "./pages/manufacturing/ConfigurationLayout";
import { ConfigurationQueuePage } from "./pages/manufacturing/ConfigurationQueuePage";
import { ConfigurationWorkPage } from "./pages/manufacturing/ConfigurationWorkPage";
import { SimActivationPage } from "./pages/manufacturing/SimActivationPage";
import { IncomingSalesOrdersPage } from "./pages/manufacturing/IncomingSalesOrdersPage";
import { ManufacturingSalesOrderDetailsPage } from "./pages/manufacturing/SalesOrderDetailsPage";
import { InventoryDiscrepancyRequestsPage } from "./pages/manufacturing/InventoryDiscrepancyRequestsPage";
import { ManufacturingSafetyReportsPage } from "./pages/manufacturing/ManufacturingSafetyReportsPage";
import { StockingOrdersPage } from "./pages/manufacturing/StockingOrdersPage";
import { TransfersWorkbenchPage } from "./pages/manufacturing/TransfersWorkbenchPage";
import { EditBatchPage } from "./pages/manufacturing/EditBatchPage";
import { BatchDetailsPage } from "./pages/manufacturing/BatchDetailsPage";
import { ManufacturingSettingsPage } from "./pages/manufacturing/ManufacturingSettingsPage";
import { BenchesPage } from "./pages/manufacturing/BenchesPage";
import { BenchDetailPage } from "./pages/manufacturing/BenchDetailPage";
import { ProductionRunPage } from "./pages/manufacturing/ProductionRunPage";
import { CmPurchaseOrderSection } from "./pages/manufacturing/CmPurchaseOrderSection";
import { DeviceLookupCoHomePage } from "./pages/manufacturing/DeviceLookupCoHomePage";
import { QcIncidentsCoHomePage } from "./pages/manufacturing/QcIncidentsCoHomePage";
import { FinalQcPage } from "./pages/manufacturing/FinalQcPage";
import { FinalQcMistakenPassPage } from "./pages/manufacturing/FinalQcMistakenPassPage";
import { EncapsulationPage } from "./pages/manufacturing/EncapsulationPage";
import { PreEncapsulationPage } from "./pages/manufacturing/PreEncapsulationPage";
import { PreEncapsulationSettingsPage } from "./pages/manufacturing/PreEncapsulationSettingsPage";
import { AssemblyWorkInstructionsPage } from "./pages/manufacturing/AssemblyWorkInstructionsPage";
import { AssemblyLayout } from "./pages/manufacturing/AssemblyLayout";
import { AssemblyQueuePage } from "./pages/manufacturing/AssemblyQueuePage";
import { AssemblyWorkOrderPage } from "./pages/manufacturing/AssemblyWorkOrderPage";
import {
  RedirectLegacyIncomingSalesOrders,
  RedirectLegacyManufacturingAlternates,
  RedirectLegacyManufacturingCmPo,
  RedirectLegacyManufacturingProductionCalendar,
  RedirectLegacyManufacturingReporting,
  RedirectLegacyManufacturingSettings,
  RedirectLegacyManufacturingWorkOrders,
  RedirectInsideSalesRepAssignment,
} from "./pages/manufacturing/ManufacturingHubLegacyRedirects";
import { DocumentsPage } from "./pages/documents/DocumentsPage";
import { WorkInstructionsPage } from "./pages/documents/WorkInstructionsPage";
import { DocumentChangeOrdersPage } from "./pages/documents/DocumentChangeOrdersPage";
import { DocumentChangeOrderDetailPage } from "./pages/documents/DocumentChangeOrderDetailPage";
import { DocumentTransitionsPage } from "./pages/documents/DocumentTransitionsPage";
import { DocumentSettingsPage } from "./pages/documents/DocumentSettingsPage";
import { DocumentCollectionDetailPage } from "./pages/documents/DocumentCollectionDetailPage";
import { DocumentCollectionsTreePage } from "./pages/documents/DocumentCollectionsTreePage";
import { DocumentDrawingCoveragePage } from "./pages/documents/DocumentDrawingCoveragePage";
import { DocumentRequestsPage } from "./pages/documents/DocumentRequestsPage";
import { WikiPage } from "./pages/WikiPage";
import { WikiDocumentPage } from "./pages/WikiDocumentPage";
import { McpAuthorizationsPage } from "./pages/integrations/McpAuthorizationsPage";
import { MyDocumentRequestsPage } from "./pages/personal/MyDocumentRequestsPage";
import { MyHubSupportTicketsPage } from "./pages/personal/MyHubSupportTicketsPage";
import { PurchaseRequestsPage } from "./pages/personal/PurchaseRequestsPage";
import { MyPackageArrivalsPage } from "./pages/personal/MyPackageArrivalsPage";
import { ReimbursementsPage } from "./pages/personal/ReimbursementsPage";
import { DfmChangeReviewPage } from "./pages/personal/DfmChangeReviewPage";
import { SafetyReportsPage } from "./pages/personal/SafetyReportsPage";
import { HomeDashboardPage } from "./pages/personal/HomeDashboardPage";
import { McpAuthorizeConsentPage } from "./pages/integrations/McpAuthorizeConsentPage";
import { McpConsentSessionGate } from "./components/McpConsentSessionGate";
import { WarehouseRecountPage } from "./pages/warehouse/WarehouseRecountPage";
import { WarehouseReturnMaterialsPage } from "./pages/warehouse/WarehouseReturnMaterialsPage";
import { WarehouseSettingsPage } from "./pages/warehouse/WarehouseSettingsPage";
import { ShipmentDetailsPage } from "./pages/shipping/ShipmentDetailsPage";
import { PaperworkPage } from "./pages/shipping/PaperworkPage";
import { ShippingHubPage } from "./pages/shipping/ShippingHubPage";
import { OneOffUpsPage } from "./pages/shipping/OneOffUpsPage";
import { ShippingSettingsPage } from "./pages/shipping/ShippingSettingsPage";
import { GosiDetailsPage } from "./pages/shipping/GosiDetailsPage";
import { InboundQueuePage } from "./pages/shipping/InboundQueuePage";
import { InboundSpecialReceiveDeskPage } from "./pages/shipping/InboundSpecialReceiveDeskPage";
import { InboundReceivePage } from "./pages/shipping/InboundReceivePage";
import { InboundReceiptReviewPage } from "./pages/shipping/InboundReceiptReviewPage";
import { CameraCaptureLayoutTestPage } from "./pages/dev/CameraCaptureLayoutTestPage";
import { ReceiveMpnKeyboardProbePage } from "./pages/dev/ReceiveMpnKeyboardProbePage";
import { ModuleNavigationMockupPage } from "./pages/dev/ModuleNavigationMockupPage";
import { InboundHoldingPage } from "./pages/shipping/InboundHoldingPage";
import { InboundArrivalsPage } from "./pages/shipping/InboundArrivalsPage";
import { InboundArrivalPackagePage } from "./pages/shipping/InboundArrivalPackagePage";
import { InboundToStockPage } from "./pages/shipping/InboundToStockPage";
import { InventoryBoxAdjustPage } from "./pages/shipping/InventoryBoxAdjustPage";
import { InventoryCartonAdjustScanPage } from "./pages/shipping/InventoryAdjustScanPage";
import { AccountsPayablePage } from "./pages/accounting/AccountsPayablePage";
import { AccountingSettingsPage } from "./pages/accounting/AccountingSettingsPage";
import { EmployeeReimbursementsPage } from "./pages/accounting/EmployeeReimbursementsPage";
import { ApCbpMonthlyStatementsPage } from "./pages/accounting/ApCbpMonthlyStatementsPage";
import { EarlyPaymentRequestsPage } from "./pages/accounting/EarlyPaymentRequestsPage";
import { ReceiptDiscrepancyPage } from "./pages/accounting/ReceiptDiscrepancyPage";
import { ApInvoiceEditPage, ApInvoiceNewPage } from "./pages/accounting/ApInvoiceCreatePage";
import { AccountsReceivablePage } from "./pages/accounting/AccountsReceivablePage";
import { CashFlowForecastPage } from "./pages/accounting/CashFlowForecastPage";
import { AccountingReportsPage } from "./pages/accounting/AccountingReportsPage";
import { SuntronicUnbilledLaborPage } from "./pages/accounting/SuntronicUnbilledLaborPage";
import { EomReconciliationPage } from "./pages/accounting/EomReconciliationPage";
import { InventoryOverTime } from "./pages/accounting/InventoryOverTime";
import { SuntronicInvoicesPage } from "./pages/accounting/SuntronicInvoicesPage";
import { SuntronicInvoiceWorkPage } from "./pages/accounting/SuntronicInvoiceWorkPage";
import { CustomersBrowsePage } from "./pages/inside-sales/CustomersBrowsePage";
import { RepAgenciesPage } from "./pages/inside-sales/RepAgenciesPage";
import { RepStaffProfilePage } from "./pages/inside-sales/RepStaffProfilePage";
import { RepAgencyDetailPage } from "./pages/inside-sales/RepAgencyDetailPage";
import { NewProductPage } from "./pages/inside-sales/NewProductPage";
import { InsideSalesApprovalsPage } from "./pages/inside-sales/InsideSalesApprovalsPage";
import { InsideSalesWirelessPortalAccountsPage } from "./pages/inside-sales/InsideSalesWirelessPortalAccountsPage";
import { InsideSalesApprovalWorkbenchPage } from "./pages/inside-sales/InsideSalesApprovalWorkbenchPage";
import { InsideSalesQuotesPage } from "./pages/inside-sales/InsideSalesQuotesPage";
import { InsideSalesReportsPage } from "./pages/inside-sales/InsideSalesReportsPage";
import { InsideSalesQuoteEditorPage } from "./pages/inside-sales/InsideSalesQuoteEditorPage";
import { InsideSalesSettingsPage } from "./pages/inside-sales/InsideSalesSettingsPage";
import { CreateSalesOrderPage } from "./pages/sales-orders/CreateSalesOrderPage";
import { SalesOrderOverview } from "./pages/sales-orders/SalesOrderOverview";
import { CorrectiveActionsPage } from "./pages/quality/CorrectiveActionsPage";
import { CorrectiveActionDetailPage } from "./pages/quality/CorrectiveActionDetailPage";
import { QualityCasesPage } from "./pages/quality/QualityCasesPage";
import { QualityCaseDetailPage } from "./pages/quality/QualityCaseDetailPage";
import { QualityEventDetailPage } from "./pages/quality/QualityEventDetailPage";
import { QualityEventsPage } from "./pages/quality/QualityEventsPage";
import { QualityRcaDetailPage } from "./pages/quality/QualityRcaDetailPage";
import { QualityRcasPage } from "./pages/quality/QualityRcasPage";
import { QualityNcrDetailPage } from "./pages/quality/QualityNcrDetailPage";
import { QualityNcrsPage } from "./pages/quality/QualityNcrsPage";
import { QualityInvestigationTravelerPage } from "./pages/quality/QualityInvestigationTravelerPage";
import { QualityQcAnalysisPage } from "./pages/quality/QualityQcAnalysisPage";
import { QualityQcAnalysisDeskPage } from "./pages/quality/QualityQcAnalysisDeskPage";
import { QualityRmasPage } from "./pages/quality/QualityRmasPage";
import { QualityRmaNewPage } from "./pages/quality/QualityRmaNewPage";
import { QualityRmaDetailPage } from "./pages/quality/QualityRmaDetailPage";
import { QualityRmaDocumentPage } from "./pages/quality/QualityRmaDocumentPage";
import { QualityFailureModesPage } from "./pages/quality/QualityFailureModesPage";
import { QualityOperatorScriptEditPage, QualityOperatorScriptsPage } from "./pages/quality/QualityOperatorScriptsPage";
import { QualitySettingsPage } from "./pages/quality/QualitySettingsPage";
import { QualityTier1ScriptEditPage, QualityTier1ScriptsPage } from "./pages/quality/QualityTier1ScriptsPage";
import { QualityTierReviewPage } from "./pages/quality/QualityTierReviewPage";
import { QualityTierReviewQueuePage } from "./pages/quality/QualityTierReviewQueuePage";
import { QualityAnalysisLoanDetailPage } from "./pages/quality/QualityAnalysisLoanDetailPage";
import { QualityAnalysisTransfersPage } from "./pages/quality/QualityAnalysisTransfersPage";
import { PrePackLayout } from "./pages/prepack/PrePackLayout";
import { PrePackQueuePage } from "./pages/prepack/PrePackQueuePage";
import { PrePackCartonPage } from "./pages/prepack/PrePackCartonPage";
import { PrePackTransferPage } from "./pages/prepack/PrePackTransferPage";
import { PersonalPrintingPage } from "./pages/personal/PersonalPrintingPage";
import { PersonalTimeClockPage } from "./pages/personal/PersonalTimeClockPage";
import { CommissionRunsPage } from "./pages/commissions/RunsPage";
import { CommissionRunDetailPage } from "./pages/commissions/RunDetailPage";
import { CommissionRulesPage } from "./pages/commissions/RulesPage";
import { CommissionStatementsPage } from "./pages/commissions/StatementsPage";
import { CommissionStockingPage } from "./pages/commissions/StockingPage";
import { CommissionEndUsersPage } from "./pages/commissions/EndUsersPage";
import { CommissionRatesPage } from "./pages/commissions/RatesPage";
import { CommissionAdjustmentsPage } from "./pages/commissions/AdjustmentsPage";
import { CommissionReconciliationPage } from "./pages/commissions/ReconciliationPage";
import { CommissionInvoiceStatusPage } from "./pages/commissions/InvoiceStatusPage";
import { FirmwareListPage } from "./pages/engineering/FirmwareListPage";
import { FirmwareDetailPage } from "./pages/engineering/FirmwareDetailPage";
import { FirmwareLogicAnalyzerPage } from "./pages/engineering/FirmwareLogicAnalyzerPage";
import { FirmwareNewPage } from "./pages/engineering/FirmwareNewPage";
import { FirmwareReleaseNoteDetailPage } from "./pages/engineering/FirmwareReleaseNoteDetailPage";
import { FirmwareReleaseNoteNewPage } from "./pages/engineering/FirmwareReleaseNoteNewPage";
import { EngineeringSettingsPage } from "./pages/engineering/EngineeringSettingsPage";
import { WirelessDeviceReportPage } from "./pages/engineering/WirelessDeviceReportPage";
import { WirelessDeviceDetailPage } from "./pages/wireless/WirelessDeviceDetailPage";
import { WirelessDevicesPage } from "./pages/wireless/WirelessDevicesPage";
import { WirelessOrganizationDetailPage } from "./pages/wireless/WirelessOrganizationDetailPage";
import { WirelessOrganizationsPage } from "./pages/wireless/WirelessOrganizationsPage";
import { WirelessReportsPage } from "./pages/wireless/WirelessReportsPage";
import { WirelessUsersPage } from "./pages/wireless/WirelessUsersPage";
import { LabelOrdersPage as ArtAndLabelsLabelOrdersPage } from "./pages/art-and-labels/LabelOrdersPage";
import { LabelSkusPage as ArtAndLabelsLabelSkusPage } from "./pages/art-and-labels/LabelSkusPage";
import { ArtFilesPage as ArtAndLabelsArtFilesPage } from "./pages/art-and-labels/ArtFilesPage";
import { LabelOrdersPage as PrintingLabelOrdersPage } from "./pages/printing/LabelOrdersPage";
import { LabelSkusPage as PrintingLabelSkusPage } from "./pages/printing/LabelSkusPage";
import { ArtFilesPage as PrintingArtFilesPage } from "./pages/printing/ArtFilesPage";
function MrpAmlCpnToSkuRedirect() {
  const { cpn } = useParams<{ cpn: string }>();
  const decoded = cpn != null && cpn.length > 0 ? decodeURIComponent(cpn) : "";
  if (decoded.length === 0) return <Navigate to="/mrp/aml" replace />;
  return <Navigate to={buildMrpSkuDetailPath(decoded, { tab: "aml" })} replace />;
}

/** RCAs used to live under the RMA. One RCA page now: /quality/rcas/:id. */
function QualityRmaRcaLegacyRedirect() {
  const { id, rcaId } = useParams<{ id: string; rcaId: string }>();
  const parsed = Number(rcaId);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return <Navigate to={id != null && id !== "" ? `/quality/rmas/${id}` : "/quality/rmas"} replace />;
  }
  return <Navigate to={`/quality/rcas/${parsed}`} replace />;
}

function ClampQcLegacyJobRedirect() {
  const { slug } = useParams<{ slug: string }>();
  if (slug == null || slug.trim() === "") {
    return <Navigate to="/clamp-qc/inspection" replace />;
  }
  return <Navigate to={`/clamp-qc/jobs/${encodeURIComponent(slug.trim())}`} replace />;
}

function ClampQcLegacyReportingJobRedirect() {
  const { slug } = useParams<{ slug: string }>();
  if (slug == null || slug.trim() === "") {
    return <Navigate to="/clamp-qc/reporting" replace />;
  }
  return <Navigate to={`/clamp-qc/reporting/jobs/${encodeURIComponent(slug.trim())}`} replace />;
}

function InventoryLocationLabelRedirect() {
  const { sku: skuParam = "", locationId = "" } = useParams();
  let sku = skuParam;
  try {
    sku = decodeURIComponent(skuParam);
  } catch {
    sku = skuParam;
  }
  const location = Number.parseInt(locationId, 10);
  if (sku.trim() === "" || !Number.isFinite(location) || location <= 0) {
    return <Navigate to="/warehouse/recount" replace />;
  }
  const params = new URLSearchParams({ sku: sku.trim(), location: String(location) });
  return <Navigate to={`/warehouse/recount?${params.toString()}`} replace />;
}

function InboundLegacyPathRedirect() {
  const { source, id } = useParams<{ source: string; id: string }>();
  if (source == null || source === "" || id == null || id === "") {
    return <Navigate to="/shipping/receive" replace />;
  }
  return <Navigate to={`/shipping/receive/${source}/${id}`} replace />;
}

function InsideSalesEndUsersRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/inside-sales/customers${search}`} replace />;
}

function RedirectMrpCustomersToInsideSales() {
  const { fsAccountId } = useParams();
  const { search } = useLocation();
  const trimmed = fsAccountId?.trim() ?? "";
  if (trimmed !== "") {
    return (
      <Navigate to={`/inside-sales/customers?fs_account_id=${encodeURIComponent(trimmed)}`} replace />
    );
  }
  return <Navigate to={`/inside-sales/customers${search}`} replace />;
}

function PurchaseOrderNewRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    void createHubPurchaseOrderAndNavigate(navigate, { location_code: "sgs" });
  }, [navigate]);
  return <p className="text-sm text-slate-600">Creating purchase order...</p>;
}

/** Tiny fallback so /mrp/forecast paints immediately when navigating from another route. */
function ForecastRouteFallback() {
  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Forecast</h1>
        <p className="mt-2 text-sm text-slate-500">Loading this page...</p>
      </header>
    </div>
  );
}

/** Module-scoped lazy only (not inside a component) - required for safe Suspense with React Router. */
const ForecastPage = lazy(() =>
  import("./pages/mrp/Forecast").then((m) => ({ default: m.Forecast })),
);

/**
 * After the first visit, the lazy chunk is cached so Suspense resolves in the same turn and the
 * large Forecast tree can block painting - the previous route stays on screen until then. Defer
 * mounting the lazy page until after the first paint so every navigation shows the shell first.
 */
function ForecastRoute() {
  const [readyForHeavyTree, setReadyForHeavyTree] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      if (!cancelled) setReadyForHeavyTree(true);
    }, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, []);
  if (!readyForHeavyTree) {
    return <ForecastRouteFallback />;
  }
  return (
    <Suspense fallback={<ForecastRouteFallback />}>
      <ForecastPage />
    </Suspense>
  );
}

export function App() {
  return (
    <Routes>
      {import.meta.env.DEV ? (
        <>
          <Route path="/dev/receive-mpn-keyboard-probe" element={<ReceiveMpnKeyboardProbePage />} />
          <Route path="/dev/module-navigation-mockup" element={<Navigate to="/dev/module-navigation-mockup/pc" replace />} />
          <Route path="/dev/module-navigation-mockup/pc" element={<ModuleNavigationMockupPage layout="pc" />} />
          <Route path="/dev/module-navigation-mockup/tablet" element={<ModuleNavigationMockupPage layout="tablet" />} />
        </>
      ) : null}
      <Route path="/sign-in" element={<SignIn />} />
      <Route path="/oauth/qbt/callback" element={<QuickBooksTimeOAuthCallbackPage />} />
      <Route path="/tablet-pin" element={<TabletPinPage />} />
      <Route path="/server-access-invite/:token" element={<ServerAccessInvitePage />} />
      <Route
        path="/integrations/mcp/authorize"
        element={
          <McpConsentSessionGate>
            <McpAuthorizeConsentPage />
          </McpConsentSessionGate>
        }
      />
      <Route element={<Protected />}>
        <Route element={<AuthenticatedHubShell />}>
        <Route path="/" element={<AppRedirect />} />
        <Route path="/floor" element={<FloorTabletHomePage />} />
        <Route path="/settings" element={<Navigate to="/personal/settings" replace />} />
        <Route path="/notifications" element={<Navigate to="/personal/notifications" replace />} />
        <Route path="/integrations/mcp" element={<Navigate to="/personal/chatgpt" replace />} />
        <Route path="/access-needed" element={<AccessNeededPage />} />
        <Route
          path="/a"
          element={
            <SubappAccessGuard appKey="shipping">
              <Layout app="shipping" />
            </SubappAccessGuard>
          }
        >
          <Route path=":scanHex" element={<InboundArrivalPackagePage />} />
        </Route>
        <Route
          path="/i"
          element={
            <SubappAccessGuard appKey="warehouse">
              <Layout app="warehouse" />
            </SubappAccessGuard>
          }
        >
          <Route index element={<Navigate to="/warehouse/carton-adjust" replace />} />
          <Route path=":boxId" element={<InventoryBoxAdjustPage />} />
        </Route>
        <Route
          path="/inv"
          element={
            <SubappAccessGuard appKey="warehouse">
              <Layout app="warehouse" />
            </SubappAccessGuard>
          }
        >
          <Route index element={<Navigate to="/warehouse/recount" replace />} />
          <Route path=":sku/:locationId" element={<InventoryLocationLabelRedirect />} />
        </Route>
        <Route path="/users/*" element={<Navigate to="/admin" replace />} />
        <Route
          path="/erp"
          element={
            <SubappAccessGuard appKey="erp">
              <Layout app="erp" />
            </SubappAccessGuard>
          }
        >
          <Route index element={<ModuleHome moduleKey="erp" />} />
          <Route path="mv-ingest" element={<Dashboard />} />
          <Route path="erpnext-sync" element={<ErpNextSync />} />
          <Route path="entity/:entity" element={<EntityDetail />} />
          <Route path="findings" element={<Findings />} />
          <Route path="data-control-registry" element={<DataControlRegistryPage />} />
          <Route path="sweep-row-registry" element={<Navigate to="/erp/data-control-registry" replace />} />
          <Route path="settings" element={<ErpSettings />} />
        </Route>
        <Route
          path="/admin"
          element={
            <SubappAccessGuard appKey="admin">
              <Layout app="admin" />
            </SubappAccessGuard>
          }
        >
          <Route index element={<ModuleHome moduleKey="admin" />} />
          <Route path="hub-accounts" element={<UserDirectory />} />
          <Route path="directory" element={<Navigate to="/people/directory" replace />} />
          <Route path="contacts" element={<ContactsPage />} />
          <Route path="module-access" element={<SubappAccessPage />} />
          <Route path="subapp-access" element={<Navigate to="/admin/module-access" replace />} />
          <Route path="megaventory-payment-terms" element={<MegaventoryPaymentTermsLabels />} />
          <Route path="distributor-apis" element={<DistributorApis />} />
          <Route path="label-templates" element={<LabelTemplatesPage />} />
          <Route path="zpl-printers" element={<ZplPrintersPage />} />
          <Route path="product-scan-links" element={<ProductScanLinksPage />} />
          <Route path="pdf-templates" element={<PdfTemplatesPage />} />
          <Route path="settings" element={<AdminSettingsPage />} />
          <Route path="tickets" element={<HubSupportTicketsPage />} />
          <Route path="vendor-portal" element={<VendorPortalPage />} />
          <Route path="server-access" element={<ServerAccessPage />} />
          <Route path="locations" element={<Locations />} />
          <Route path="labor" element={<AdminLaborPage />} />
          <Route path="time-card-changes" element={<AdminTimeCardChangesPage />} />
          <Route path="erp-locations" element={<Navigate to="/admin/locations" replace />} />
          <Route path="user-settings" element={<Navigate to="/people/directory" replace />} />
          <Route path="staff" element={<Navigate to="/people/directory" replace />} />
        </Route>
        <Route
          path="/people"
          element={
            <SubappAccessGuard appKey="people">
              <Layout app="people" />
            </SubappAccessGuard>
          }
        >
          <Route index element={<ModuleHome moduleKey="people" />} />
          <Route path="directory" element={<PeopleDirectoryPage />} />
          <Route path="org" element={<PeopleOrgChartPage />} />
          <Route path="training" element={<PeopleTrainingMatrixPage />} />
          <Route path="skills" element={<PeopleSkillsPage />} />
          <Route path="permissions" element={<PeoplePermissionsPage />} />
          <Route path="apps" element={<PeopleAppsPage />} />
          <Route path="staff/:staffId" element={<PersonPage />} />
        </Route>
        <Route path="/mrp/bom" element={<MrpBomToBomAppRedirect />} />
        <Route
          path="/clamp-qc"
          element={
            <SubappAccessGuard appKey="clampqc">
              <Layout app="clampqc" />
            </SubappAccessGuard>
          }
        >
          <Route index element={<ModuleHome moduleKey="clampqc" />} />
          <Route path="inspection" element={<ClampQcHubPage />} />
          <Route path="reporting/jobs/:slug" element={<ClampQcReportingWorkOrderLinesPage />} />
          <Route path="reporting/work-orders/:slug" element={<ClampQcLegacyReportingJobRedirect />} />
          <Route path="reporting" element={<ClampQcReportingPage />} />
          <Route path="settings" element={<ClampQcSettingsPage />} />
          <Route path="jobs/:slug" element={<ClampQcPage />} />
          <Route path="receive/:slug" element={<ClampQcReceivePage />} />
          <Route path="work-orders/:slug" element={<ClampQcLegacyJobRedirect />} />
        </Route>
        <Route path="/manufacturing/incoming-sales-orders" element={<RedirectLegacyIncomingSalesOrders />} />
        <Route path="/manufacturing/cm-po/*" element={<RedirectLegacyManufacturingCmPo />} />
        <Route path="/manufacturing/alternates" element={<RedirectLegacyManufacturingAlternates />} />
        <Route path="/manufacturing/production-calendar" element={<RedirectLegacyManufacturingProductionCalendar />} />
        <Route path="/manufacturing/reporting" element={<RedirectLegacyManufacturingReporting />} />
        <Route path="/manufacturing/work-orders" element={<RedirectLegacyManufacturingWorkOrders />} />
        <Route path="/manufacturing/settings" element={<RedirectLegacyManufacturingSettings />} />
        <Route
          path="/manufacturing/qc-incidents"
          element={
            <SubappAccessGuardAnyOf appKeys={moduleKeysListingNavPath("/manufacturing/qc-incidents")}>
              <Layout app="manufacturing" />
            </SubappAccessGuardAnyOf>
          }
        >
          <Route index element={<QcIncidentsCoHomePage />} />
        </Route>
        <Route
          path="/manufacturing/device-lookup"
          element={
            <SubappAccessGuardAnyOf appKeys={moduleKeysListingNavPath("/manufacturing/device-lookup")}>
              <Layout app="manufacturing" />
            </SubappAccessGuardAnyOf>
          }
        >
          <Route index element={<DeviceLookupCoHomePage />} />
        </Route>
        <Route
          path="/manufacturing"
          element={
            <SubappAccessGuard appKey="manufacturing">
              <Layout app="manufacturing" />
            </SubappAccessGuard>
          }
        >
          <Route index element={<ModuleHome moduleKey="manufacturing" />} />
          <Route path="kitting" element={<KittingPage />} />
          <Route path="kitting/pick" element={<KittingPickPage />} />
          <Route path="configuration" element={<ConfigurationLayout />}>
            <Route index element={<ConfigurationQueuePage />} />
            <Route path="build/:batchItemId" element={<ConfigurationWorkPage />} />
            <Route path="alternate-order/:alternateOrderId" element={<ConfigurationWorkPage />} />
            <Route path="alternate" element={<ConfigurationWorkPage />} />
          </Route>
          <Route path="laser-mark" element={<LaserMarkPage />} />
          <Route path="benches" element={<BenchesPage />} />
          <Route path="benches/:machineId" element={<BenchDetailPage />} />
          <Route path="wireless-check" element={<WirelessCheckPage />} />
          <Route path="cal-test" element={<ProductionRunPage />} />
          <Route path="production" element={<Navigate to="/manufacturing/cal-test" replace />} />
          <Route path="sim" element={<SimActivationPage />} />
          <Route path="sim-activation" element={<Navigate to="/manufacturing/sim" replace />} />
          <Route path="assembly" element={<AssemblyLayout />}>
            <Route index element={<AssemblyQueuePage />} />
            <Route path="work-orders/:buildOrderId" element={<AssemblyWorkOrderPage />} />
          </Route>
          <Route path="magnet-packaging" element={<Navigate to="/manufacturing/assembly" replace />} />
          <Route path="final-qc/mistaken-pass" element={<FinalQcMistakenPassPage />} />
          <Route path="final-qc" element={<FinalQcPage />} />
          <Route path="pre-encapsulation/settings" element={<PreEncapsulationSettingsPage />} />
          <Route path="pre-encapsulation" element={<PreEncapsulationPage />} />
          <Route path="encapsulation" element={<EncapsulationPage />} />
        </Route>
        <Route
          path="/manufacturing-management/inventory-discrepancies"
          element={
            <SubappAccessGuardAnyOf appKeys={moduleKeysListingNavPath("/manufacturing-management/inventory-discrepancies")}>
              <Layout app="manufacturing_management" />
            </SubappAccessGuardAnyOf>
          }
        >
          <Route index element={<InventoryDiscrepancyRequestsPage />} />
        </Route>
        <Route
          path="/manufacturing-management"
          element={
            <SubappAccessGuard appKey="manufacturing_management">
              <Layout app="manufacturing_management" />
            </SubappAccessGuard>
          }
        >
          <Route index element={<ModuleHome moduleKey="manufacturing_management" />} />
          <Route path="incoming-orders" element={<IncomingSalesOrdersPage />} />
          <Route path="sales-order-details/:salesOrderId?" element={<ManufacturingSalesOrderDetailsPage />} />
          <Route path="batch-details" element={<BatchDetailsPage />} />
          <Route path="forward-demand" element={<ForwardDemandPage />} />
          <Route path="production-capacity" element={<ProductionCapacityPage />} />
          <Route path="production-capacity/settings" element={<ProductionCapacitySettingsPage />} />
          <Route path="settings" element={<ManufacturingSettingsPage />} />
          <Route path="edit-batch" element={<EditBatchPage />} />
          <Route path="cm-po/*" element={<CmPurchaseOrderSection />} />
          <Route path="work-orders" element={<WorkOrdersPage />} />
          <Route path="work-orders/:buildOrderId" element={<WorkOrdersPage />} />
          <Route path="assembly-work-instructions" element={<AssemblyWorkInstructionsPage />} />
          <Route path="alternates" element={<AlternatesPage />} />
          <Route path="production-calendar" element={<ProductionCalendarPage />} />
          <Route path="reporting" element={<ManufacturingReportingPage />} />
          <Route path="potting-send" element={<Navigate to="/manufacturing-management/potting-po" replace />} />
          <Route path="potting-po" element={<PottingSendPage />} />
          <Route path="box-label-generate" element={<BoxLabelGeneratePage />} />
          <Route path="suntronic-box-labels" element={<SuntronicBoxLabelsPage />} />
          <Route path="clamp-qc-credit" element={<ClampQcCreditRequestsPage />} />
          <Route path="wireless-issue" element={<WirelessIssuePage />} />
          <Route path="wireless-assignments" element={<WirelessAssignmentsPage />} />
          <Route path="safety-reports" element={<ManufacturingSafetyReportsPage />} />
          <Route path="stocking-orders" element={<StockingOrdersPage />} />
          <Route path="transfers" element={<TransfersWorkbenchPage />} />
        </Route>
        <Route
          path="/documents"
          element={
            <SubappAccessGuard appKey="documents">
              <Layout app="documents" />
            </SubappAccessGuard>
          }
        >
          <Route index element={<ModuleHome moduleKey="documents" />} />
          <Route path="workbench" element={<DocumentsPage />} />
          <Route path="work-instructions" element={<WorkInstructionsPage />} />
          <Route path="work-instructions/:documentId" element={<WorkInstructionsPage />} />
          <Route path="collections" element={<DocumentCollectionsTreePage />} />
          <Route path="collections/:collectionId" element={<DocumentCollectionDetailPage />} />
          <Route path="settings" element={<DocumentSettingsPage />} />
          <Route path="admin" element={<Navigate to="/documents/settings" replace />} />
          <Route path="notes" element={<Navigate to="/documents/settings" replace />} />
          <Route path="families" element={<Navigate to="/documents/settings" replace />} />
          <Route path="transitions" element={<DocumentTransitionsPage />} />
          <Route path="change-orders" element={<DocumentChangeOrdersPage />} />
          <Route path="change-orders/:id" element={<DocumentChangeOrderDetailPage />} />
          <Route path="drawing-coverage" element={<DocumentDrawingCoveragePage />} />
          <Route path="requests" element={<DocumentRequestsPage />} />
        </Route>
        <Route
          path="/wiki"
          element={
            <SubappAccessGuard appKey="wiki">
              <Layout app="wiki" />
            </SubappAccessGuard>
          }
        >
          <Route index element={<WikiPage />} />
          <Route path="docs/:id" element={<WikiDocumentPage />} />
        </Route>
        <Route
          path="/mrp/purchase-invoices"
          element={
            <SubappAccessGuardAnyOf appKeys={moduleKeysListingNavPath("/mrp/purchase-invoices")}>
              <Layout app="mrp" />
            </SubappAccessGuardAnyOf>
          }
        >
          <Route index element={<PurchaseInvoiceDetailPage />} />
          <Route path=":id" element={<PurchaseInvoiceDetailPage />} />
        </Route>
        <Route
          path="/mrp"
          element={
            <SubappAccessGuard appKey="mrp">
              <Layout app="mrp" />
            </SubappAccessGuard>
          }
        >
          <Route index element={<ModuleHome moduleKey="mrp" />} />
          <Route path="planning" element={<Planning />} />
          <Route path="tla-planning" element={<TlaPlanningPage />} />
          <Route path="tla-planning/detail" element={<TlaPlanningDetailPage />} />
          <Route path="forecast" element={<ForecastRoute />} />
          <Route path="ghost-build-orders" element={<GhostBuildOrders />} />
          <Route path="clamp-forecast" element={<ClampForecast />} />
          <Route path="build-plan" element={<BuildPlan />} />
          <Route path="assemblies" element={<Assemblies />} />
          <Route path="skus" element={<Skus />} />
          <Route path="skus/*" element={<SkuDetail />} />
          <Route path="adjustments/:id" element={<AdjustmentDetailPage />} />
          <Route path="safety-stock-alerts" element={<SafetyStockAlerts />} />
          <Route path="overstock" element={<Overstock />} />
          <Route path="aml/cpn/:cpn" element={<MrpAmlCpnToSkuRedirect />} />
          <Route path="aml" element={<Aml />} />
          <Route path="purchase-orders" element={<Outlet />}>
            <Route index element={<PurchaseOrderList />} />
            <Route path="new" element={<PurchaseOrderNewRedirect />} />
            <Route path="compose" element={<PurchaseOrderComposeLegacyRedirect />} />
            <Route path=":id" element={<PurchaseOrderDetail />} />
          </Route>
          <Route path="rfqs" element={<Outlet />}>
            <Route index element={<RfqList />} />
            <Route path="new" element={<Navigate to="/mrp/rfqs/compose" replace />} />
            <Route path=":id" element={<RfqDetail />} />
          </Route>
          <Route path="customers" element={<RedirectMrpCustomersToInsideSales />} />
          <Route path="customers/:fsAccountId" element={<RedirectMrpCustomersToInsideSales />} />
          <Route path="suppliers" element={<Outlet />}>
            <Route index element={<SupplierListPage />} />
            <Route path="new" element={<SupplierNewPage />} />
            <Route path=":id" element={<SupplierLegacyDetailRedirect />} />
          </Route>
          <Route path="sales-orders" element={<Outlet />}>
            <Route index element={<RedirectMrpSalesOrders />} />
            <Route path="new" element={<Navigate to="/inside-sales/sales-orders/new" replace />} />
            <Route path=":id" element={<RedirectMrpSalesOrderId />} />
          </Route>
          <Route path="incoming-parts" element={<IncomingParts />} />
          <Route path="transmute" element={<TransmutePage />} />
          <Route path="settings" element={<MrpSettings />} />
        </Route>
        <Route
          path="/bom"
          element={
            <SubappAccessGuard appKey="bom">
              <Layout app="bom" />
            </SubappAccessGuard>
          }
        >
          <Route index element={<ModuleHome moduleKey="bom" />} />
          <Route path="browse" element={<BomBrowser />} />
          <Route path="set-bom-rules" element={<SetBomRules />} />
          <Route path="test-revision" element={<BomTestRevisionPage />} />
          <Route path="push-megaventory" element={<PushToMegaventoryPage />} />
          <Route path="approve-bom-rules" element={<ApproveBomRules />} />
        </Route>
        <Route
          path="/accounting"
          element={
            <SubappAccessGuard appKey="accounting">
              <Layout app="accounting" />
            </SubappAccessGuard>
          }
        >
          <Route index element={<ModuleHome moduleKey="accounting" />} />
          <Route path="ap" element={<AccountsPayablePage />} />
          <Route path="early-payment-requests" element={<EarlyPaymentRequestsPage />} />
          <Route path="cbp-monthly-statements" element={<ApCbpMonthlyStatementsPage />} />
          <Route path="ap/new" element={<ApInvoiceNewPage />} />
          <Route path="ap/:invoiceId/edit" element={<ApInvoiceEditPage />} />
          <Route path="accounts-receivable" element={<AccountsReceivablePage />} />
          <Route path="cash-flow" element={<CashFlowForecastPage />} />
          <Route path="inventory-over-time" element={<InventoryOverTime />} />
          <Route path="eom-reconciliation" element={<EomReconciliationPage />} />
          <Route path="reports" element={<AccountingReportsPage />} />
          <Route path="suntronic-unbilled-labor" element={<SuntronicUnbilledLaborPage />} />
          <Route path="suntronic-invoices" element={<SuntronicInvoicesPage />} />
          <Route path="suntronic-invoices/:invoiceId" element={<SuntronicInvoiceWorkPage />} />
          <Route path="holding-review" element={<Navigate to="/accounting/receipt-discrepancy" replace />} />
          <Route path="receipt-discrepancy" element={<ReceiptDiscrepancyPage />} />
          <Route path="reimbursements/*" element={<EmployeeReimbursementsPage />} />
          <Route path="receipt-discrepancies" element={<Navigate to="/accounting/receipt-discrepancy" replace />} />
          <Route path="settings" element={<AccountingSettingsPage />} />
          <Route path="qbo-aging" element={<Navigate to="/accounting/cash-flow" replace />} />
        </Route>
        <Route
          path="/quality/devices"
          element={
            <SubappAccessGuardAnyOf appKeys={moduleKeysListingNavPath("/quality/devices")}>
              <Layout app="quality" />
            </SubappAccessGuardAnyOf>
          }
        >
          <Route index element={<DeviceLookupCoHomePage />} />
        </Route>
        <Route
          path="/quality/qc-incidents"
          element={
            <SubappAccessGuardAnyOf appKeys={moduleKeysListingNavPath("/quality/qc-incidents")}>
              <Layout app="quality" />
            </SubappAccessGuardAnyOf>
          }
        >
          <Route index element={<QcIncidentsCoHomePage />} />
        </Route>
        <Route
          path="/quality"
          element={
            <SubappAccessGuard appKey="quality">
              <Layout app="quality" />
            </SubappAccessGuard>
          }
        >
          <Route index element={<ModuleHome moduleKey="quality" />} />
          <Route path="qc-analysis" element={<QualityQcAnalysisPage />} />
          <Route path="qc-analysis/:id" element={<QualityQcAnalysisDeskPage />} />
          <Route path="rmas" element={<QualityRmasPage />} />
          <Route path="analysis-loans" element={<QualityAnalysisTransfersPage />} />
          <Route path="analysis-loans/:transferId" element={<QualityAnalysisLoanDetailPage />} />
          <Route path="rmas/new" element={<QualityRmaNewPage />} />
          <Route path="rmas/:id/document" element={<QualityRmaDocumentPage />} />
          <Route path="rmas/:id/rca/:rcaId" element={<QualityRmaRcaLegacyRedirect />} />
          <Route path="rmas/:id" element={<QualityRmaDetailPage />} />
          <Route path="cases" element={<QualityCasesPage />} />
          <Route path="cases/:id/traveler" element={<QualityInvestigationTravelerPage />} />
          <Route
            path="cases/:id/investigations/:investigationId/traveler"
            element={<QualityInvestigationTravelerPage />}
          />
          <Route path="cases/:id" element={<QualityCaseDetailPage />} />
          <Route path="events" element={<QualityEventsPage />} />
          <Route path="events/:id" element={<QualityEventDetailPage />} />
          <Route path="ncrs" element={<QualityNcrsPage />} />
          <Route path="ncrs/:id" element={<QualityNcrDetailPage />} />
          <Route path="tier-1-review" element={<QualityTierReviewQueuePage tier={1} />} />
          <Route path="tier-1-review/:incidentId" element={<QualityTierReviewPage tier={1} />} />
          <Route path="tier-2-review" element={<QualityTierReviewQueuePage tier={2} />} />
          <Route path="tier-2-review/:incidentId" element={<QualityTierReviewPage tier={2} />} />
          <Route path="rcas" element={<QualityRcasPage />} />
          <Route path="rcas/:id" element={<QualityRcaDetailPage />} />
          <Route path="corrective-actions" element={<CorrectiveActionsPage />} />
          <Route path="corrective-actions/:id" element={<CorrectiveActionDetailPage />} />
          <Route path="engineering-changes" element={<DocumentChangeOrdersPage />} />
          <Route path="engineering-changes/:id" element={<DocumentChangeOrderDetailPage />} />
          <Route path="settings/failure-modes" element={<QualityFailureModesPage />} />
          <Route path="settings/operator-scripts/new" element={<QualityOperatorScriptEditPage />} />
          <Route path="settings/operator-scripts/:scriptId" element={<QualityOperatorScriptEditPage />} />
          <Route path="settings/operator-scripts" element={<QualityOperatorScriptsPage />} />
          <Route path="settings/tier-1-scripts/new" element={<QualityTier1ScriptEditPage />} />
          <Route path="settings/tier-1-scripts/:issueOptionId" element={<QualityTier1ScriptEditPage />} />
          <Route path="settings/tier-1-scripts" element={<QualityTier1ScriptsPage />} />
          <Route path="settings" element={<QualitySettingsPage />} />
        </Route>
        <Route
          path="/shipping/gosi-details"
          element={
            <SubappAccessGuardAnyOf appKeys={moduleKeysListingNavPath("/shipping/gosi-details")}>
              <Layout app="shipping" />
            </SubappAccessGuardAnyOf>
          }
        >
          <Route index element={<GosiDetailsPage />} />
        </Route>
        <Route
          path="/shipping"
          element={
            <SubappAccessGuard appKey="shipping">
              <Layout app="shipping" />
            </SubappAccessGuard>
          }
        >
          <Route index element={<ModuleHome moduleKey="shipping" />} />
          <Route path="shipment-details" element={<ShipmentDetailsPage />} />
          <Route path="outbound" element={<ShippingHubPage />} />
          <Route path="one-off" element={<OneOffUpsPage />} />
          <Route path="paperwork" element={<PaperworkPage />} />
          <Route path="settings" element={<ShippingSettingsPage />} />
          <Route path="pre-pack" element={<PrePackLayout />}>
            <Route index element={<PrePackQueuePage />} />
            <Route path="shipments/:shipmentId/cartons/:cartonNumber" element={<PrePackCartonPage />} />
            <Route path="transfers/:transferOrderId" element={<PrePackTransferPage />} />
          </Route>
          <Route path="arrivals" element={<InboundArrivalsPage />} />
          <Route path="receive" element={<InboundQueuePage />} />
          <Route path="receive/rma" element={<InboundSpecialReceiveDeskPage route="rma" />} />
          <Route
            path="receive/non-po-purchase"
            element={<InboundSpecialReceiveDeskPage route="non_po_purchase" />}
          />
          <Route path="receive/discrepancies" element={<Navigate to="/shipping/holding" replace />} />
          <Route path="receive/:source/:id" element={<InboundReceivePage />} />
          <Route path="holding" element={<InboundHoldingPage />} />
          <Route path="to-stock" element={<InboundToStockPage />} />
          <Route path="dev/camera-capture-layout" element={<CameraCaptureLayoutTestPage />} />
          <Route path="inbound/receipts/:receiptId" element={<InboundReceiptReviewPage />} />
          <Route path="inbound" element={<Navigate to="/shipping/receive" replace />} />
          <Route path="inbound/second-counts" element={<Navigate to="/shipping/holding" replace />} />
          <Route path="inbound/discrepancies" element={<Navigate to="/shipping/holding" replace />} />
          <Route path="inbound/:source/:id" element={<InboundLegacyPathRedirect />} />
          <Route path="inbound-v2" element={<Navigate to="/shipping/receive" replace />} />
          <Route path="inbound-v2/:source/:id" element={<InboundLegacyPathRedirect />} />
        </Route>
        <Route
          path="/warehouse/carton-adjust"
          element={
            <SubappAccessGuardAnyOf appKeys={moduleKeysListingNavPath("/warehouse/carton-adjust")}>
              <Layout app="warehouse" />
            </SubappAccessGuardAnyOf>
          }
        >
          <Route index element={<InventoryCartonAdjustScanPage />} />
        </Route>
        <Route path="/warehouse/location-adjust" element={<Navigate to="/warehouse/recount" replace />} />
        <Route
          path="/warehouse/recount"
          element={
            <SubappAccessGuardAnyOf appKeys={moduleKeysListingNavPath("/warehouse/recount")}>
              <Layout app="warehouse" />
            </SubappAccessGuardAnyOf>
          }
        >
          <Route index element={<WarehouseRecountPage />} />
        </Route>
        <Route path="/warehouse/location-label" element={<Navigate to="/warehouse/recount" replace />} />
        <Route
          path="/warehouse"
          element={
            <SubappAccessGuard appKey="warehouse">
              <Layout app="warehouse" />
            </SubappAccessGuard>
          }
        >
          <Route index element={<ModuleHome moduleKey="warehouse" />} />
          <Route path="recount" element={<Navigate to="/warehouse/recount" replace />} />
          <Route path="return-materials" element={<WarehouseReturnMaterialsPage />} />
          <Route
            path="carton-adjustments"
            element={<Navigate to="/manufacturing-management/inventory-discrepancies" replace />}
          />
          <Route path="settings" element={<WarehouseSettingsPage />} />
        </Route>
        <Route
          path="/inside-sales"
          element={
            <SubappAccessGuard appKey="inside_sales">
              <Layout app="inside_sales" />
            </SubappAccessGuard>
          }
        >
          <Route index element={<ModuleHome moduleKey="inside_sales" />} />
          <Route path="approvals" element={<InsideSalesApprovalsPage />} />
          <Route path="wireless-portal-accounts" element={<InsideSalesWirelessPortalAccountsPage />} />
          <Route path="reports" element={<InsideSalesReportsPage />} />
          <Route path="approvals/:approvalId" element={<InsideSalesApprovalWorkbenchPage />} />
          <Route path="quotes" element={<Outlet />}>
            <Route index element={<InsideSalesQuotesPage />} />
            <Route path="new" element={<InsideSalesQuoteEditorPage />} />
            <Route path=":quoteId" element={<InsideSalesQuoteEditorPage />} />
          </Route>
          <Route path="settings" element={<InsideSalesSettingsPage />} />
          <Route path="rep-agencies" element={<RepAgenciesPage />} />
          <Route path="rep-staff/:staffId" element={<RepStaffProfilePage />} />
          <Route path="rep-agencies/:agencyId" element={<RepAgencyDetailPage />} />
          <Route path="customers" element={<CustomersBrowsePage />} />
          <Route path="end-users" element={<InsideSalesEndUsersRedirect />} />
          <Route path="sales-orders" element={<Outlet />}>
            <Route index element={<SalesOrderOverview />} />
            <Route path="new" element={<CreateSalesOrderPage />} />
            <Route path="approvals" element={<Navigate to="/inside-sales/approvals" replace />} />
            <Route path="settings" element={<Navigate to="/inside-sales/settings" replace />} />
            <Route path="rep-assignment" element={<RedirectInsideSalesRepAssignment />} />
            <Route path="rep-assignment/:salesOrderId" element={<RedirectInsideSalesRepAssignment />} />
            <Route path=":orderId" element={<CreateSalesOrderPage />} />
          </Route>
          <Route path="new-product" element={<NewProductPage />} />
        </Route>
        <Route path="/sales-orders" element={<RedirectSalesOrdersToInsideSales />} />
        <Route path="/sales-orders/*" element={<RedirectSalesOrdersToInsideSales />} />
        <Route
          path="/marketing"
          element={
            <SubappAccessGuard appKey="marketing">
              <Layout app="marketing" />
            </SubappAccessGuard>
          }
        >
          <Route index element={<ModuleHome moduleKey="marketing" />} />
        </Route>
        <Route
          path="/wireless"
          element={
            <SubappAccessGuard appKey="wireless">
              <Layout app="wireless" />
            </SubappAccessGuard>
          }
        >
          <Route index element={<ModuleHome moduleKey="wireless" />} />
          <Route path="reports" element={<WirelessReportsPage />} />
          <Route path="checkins" element={<Navigate to="/wireless/reports?tab=checkins" replace />} />
          <Route path="devices" element={<WirelessDevicesPage />} />
          <Route path="devices/:deviceId" element={<WirelessDeviceDetailPage />} />
          <Route path="organizations" element={<WirelessOrganizationsPage />} />
          <Route path="organizations/:organizationId" element={<WirelessOrganizationDetailPage />} />
          <Route path="users" element={<WirelessUsersPage />} />
        </Route>
        <Route
          path="/commissions"
          element={
            <SubappAccessGuard appKey="commissions">
              <Layout app="commissions" />
            </SubappAccessGuard>
          }
        >
          <Route index element={<ModuleHome moduleKey="commissions" />} />
          <Route path="runs" element={<CommissionRunsPage />} />
          <Route path="runs/:id" element={<CommissionRunDetailPage />} />
          <Route path="rules" element={<CommissionRulesPage />} />
          <Route path="statements" element={<CommissionStatementsPage />} />
          <Route path="stocking" element={<CommissionStockingPage />} />
          <Route path="end-users" element={<CommissionEndUsersPage />} />
          <Route path="rates" element={<CommissionRatesPage />} />
          <Route path="adjustments" element={<CommissionAdjustmentsPage />} />
          <Route path="reconciliation" element={<CommissionReconciliationPage />} />
          <Route path="invoice-status" element={<CommissionInvoiceStatusPage />} />
        </Route>
        <Route
          path="/art-and-labels"
          element={
            <SubappAccessGuard appKey="art_and_labels">
              <Layout app="art_and_labels" />
            </SubappAccessGuard>
          }
        >
          <Route index element={<ModuleHome moduleKey="art_and_labels" />} />
          <Route path="orders" element={<ArtAndLabelsLabelOrdersPage />} />
          <Route path="label-skus" element={<ArtAndLabelsLabelSkusPage />} />
          <Route path="art-files" element={<ArtAndLabelsArtFilesPage />} />
        </Route>
        <Route path="/misc/printing" element={<Navigate to="/personal/printing" replace />} />
        <Route path="/misc/time-clock" element={<Navigate to="/personal/time-clock" replace />} />
        <Route path="/misc" element={<Navigate to="/personal/printing" replace />} />
        <Route
          path="/printing"
          element={
            <SubappAccessGuard appKey="printing">
              <Layout app="printing" />
            </SubappAccessGuard>
          }
        >
          <Route index element={<ModuleHome moduleKey="printing" />} />
          <Route path="orders" element={<PrintingLabelOrdersPage />} />
          <Route path="label-skus" element={<PrintingLabelSkusPage />} />
          <Route path="art-files" element={<PrintingArtFilesPage />} />
        </Route>
        <Route
          path="/engineering"
          element={
            <SubappAccessGuard appKey="engineering">
              <Layout app="engineering" />
            </SubappAccessGuard>
          }
        >
          <Route index element={<ModuleHome moduleKey="engineering" />} />
          <Route path="firmware" element={<FirmwareListPage />} />
          <Route path="firmware/new" element={<FirmwareNewPage />} />
          <Route path="firmware/notes/new" element={<FirmwareReleaseNoteNewPage />} />
          <Route path="firmware/notes/:id" element={<FirmwareReleaseNoteDetailPage />} />
          <Route path="firmware/:id/analyzer" element={<FirmwareLogicAnalyzerPage />} />
          <Route path="firmware/:id" element={<FirmwareDetailPage />} />
          <Route path="settings" element={<EngineeringSettingsPage />} />
          <Route path="wireless-device-report" element={<WirelessDeviceReportPage />} />
        </Route>
        <Route
          path="/personal"
          element={
            <SubappAccessGuard appKey="personal">
              <Layout app="personal" />
            </SubappAccessGuard>
          }
        >
          <Route index element={<ModuleHome moduleKey="personal" />} />
          <Route path="home" element={<HomeDashboardPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="time-clock" element={<PersonalTimeClockPage />} />
          <Route path="printing" element={<PersonalPrintingPage />} />
          <Route path="settings" element={<UserSettingsPage />} />
          <Route path="purchase-requests/*" element={<PurchaseRequestsPage />} />
          <Route path="reimbursements/*" element={<ReimbursementsPage />} />
          <Route path="package-arrivals/*" element={<MyPackageArrivalsPage />} />
          <Route path="document-requests/*" element={<MyDocumentRequestsPage />} />
          <Route path="tickets/*" element={<MyHubSupportTicketsPage />} />
          <Route path="dfm-change-review" element={<DfmChangeReviewPage />} />
          <Route path="safety-reports/*" element={<SafetyReportsPage />} />
          <Route path="chatgpt" element={<McpAuthorizationsPage />} />
          <Route path="mcp" element={<Navigate to="/personal/chatgpt" replace />} />
        </Route>
        <Route path="*" element={<AppRedirect />} />
        </Route>
      </Route>
    </Routes>
  );
}
